import moment from 'moment';

import {
  DEFAULT_RELATIVE_PERIOD,
  isRelativePeriodValue,
  resolvePeriodToMillis,
} from '@src/components/RelativeAbsolutePeriodFilter/resolvePeriodToMillis';
import { PERIOD_PRESET_IDS } from '@src/components/RelativeAbsolutePeriodFilter/RelativeAbsolutePeriodFilter.types';
import { translateString } from '@src/util/intl/translateString';

export const REDUCER_INDEX = 'events';
const BASE_URL = 'timeline';

export const TIMELINE_FILTER_FIELDS = {
  SEARCH: 'search',
  DATES: 'dates',
  RELATIVE: 'relative',
  ENTITY: 'entity',
  RECORD_TYPES: 'recordTypes',
};

/** Record-type filter values (schedule types + standalone event sentinel). */
export const TIMELINE_RECORD_TYPE = {
  SCHEDULE: 'schedule',
  CHECKPOINT_ROUTE: 'checkpoint_route',
  TASK: 'task',
  EVENT: 'event',
};

export const TIMELINE_RECORD_TYPE_OPTIONS = [
  {
    value: TIMELINE_RECORD_TYPE.SCHEDULE,
    labelId: 'timeline.filter.recordType.schedule',
  },
  {
    value: TIMELINE_RECORD_TYPE.CHECKPOINT_ROUTE,
    labelId: 'timeline.filter.recordType.checkpoint_route',
  },
  {
    value: TIMELINE_RECORD_TYPE.TASK,
    labelId: 'timeline.filter.recordType.task',
  },
  {
    value: TIMELINE_RECORD_TYPE.EVENT,
    labelId: 'timeline.filter.recordType.event',
  },
];

export const TIMELINE_FILTERS_STORAGE_KEY = 'timelineFilters';
const LEGACY_TIMELINE_PRESET_VALUE_TRANSLATED = Object.fromEntries(
  PERIOD_PRESET_IDS.map(presetId => [presetId, `timeline-period-preset:${presetId}`]),
);

/** First-visit Timeline window — wider than the shared 1-day period default. */
export const TIMELINE_DEFAULT_RELATIVE_PERIOD = {
  ...DEFAULT_RELATIVE_PERIOD,
  relativeAmount: 7,
};

const formatRelativeValueTranslated = relative => {
  if (!isRelativePeriodValue(relative)) {
    return '';
  }

  const unitLabel = translateString(`dateTypes.${relative.dateType}`, {
    amount: relative.relativeAmount,
  });

  return `${relative.relativeAmount} ${unitLabel}`;
};

export const INIT_FILTERS = {
  search: {
    value: '',
    label: translateString('timeline.filter.globalSearch'),
    valueTranslated: '',
    valueType: 'string',
    clearValue: '',
  },
  dates: {
    value: [],
    label: translateString('timeline.filter.dateRange'),
    valueTranslated: '',
    valueType: 'array',
    clearValue: [],
  },
  // Seeded so the first visit lands on a bounded window. Clearing the period is
  // still valid and means "all time" (no createdAtDevice bounds).
  relative: {
    value: TIMELINE_DEFAULT_RELATIVE_PERIOD,
    label: translateString('timeline.filter.period'),
    valueTranslated: formatRelativeValueTranslated(TIMELINE_DEFAULT_RELATIVE_PERIOD),
    valueType: 'string',
    clearValue: null,
    hideWhenCleared: true,
  },
  entity: {
    value: '',
    label: translateString('timeline.filter.entity'),
    valueTranslated: '',
    valueType: 'string',
    clearValue: '',
  },
  recordTypes: {
    value: [],
    label: translateString('timeline.filter.recordType'),
    valueTranslated: '',
    valueType: 'array',
    clearValue: [],
  },
};

const isFilterValuesFormat = filters =>
  filters?.search && typeof filters.search === 'object' && 'value' in filters.search;

const formatStoredDates = storedDates => {
  if (!Array.isArray(storedDates) || storedDates.length !== 2) {
    return { value: [], valueTranslated: '' };
  }

  const isoDates = storedDates.map(date => moment(date).toISOString());
  const valueTranslated = `${moment(isoDates[0]).format('YYYY-MM-DD HH:mm')} - ${moment(
    isoDates[1],
  ).format('YYYY-MM-DD HH:mm')}`;

  return { value: isoDates, valueTranslated };
};

const formatRecordTypesTranslated = recordTypes => {
  if (!Array.isArray(recordTypes) || recordTypes.length === 0) {
    return '';
  }

  return recordTypes
    .map(value => {
      const option = TIMELINE_RECORD_TYPE_OPTIONS.find(item => item.value === value);
      return option ? translateString(option.labelId) : value;
    })
    .join(', ');
};

const isTimelinePresetValue = value =>
  Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      value.isRelative === false &&
      value.mode === 'preset' &&
      PERIOD_PRESET_IDS.includes(value.presetId),
  );

const getLegacyStoredPresetId = valueTranslated =>
  PERIOD_PRESET_IDS.find(
    presetId => LEGACY_TIMELINE_PRESET_VALUE_TRANSLATED[presetId] === valueTranslated,
  ) ?? null;

const normalizeStoredRelative = (storedRelative, valueTranslated = '') => {
  if (isRelativePeriodValue(storedRelative)) {
    const relative = {
      ...DEFAULT_RELATIVE_PERIOD,
      ...storedRelative,
      isRelative: true,
    };

    return {
      value: relative,
      valueTranslated: formatRelativeValueTranslated(relative),
      valueType: 'string',
    };
  }

  const presetId = isTimelinePresetValue(storedRelative)
    ? storedRelative.presetId
    : getLegacyStoredPresetId(valueTranslated);

  return {
    value: presetId ? { isRelative: false, mode: 'preset', presetId } : null,
    valueTranslated: '',
    valueType: 'null',
  };
};

const hasAbsoluteRange = dates => Array.isArray(dates) && dates.length === 2;

/**
 * Builds the period value used by RelativeAbsolutePeriodFilter from FilterValues.
 */
export const getTimelinePeriodValue = filters => {
  const relativeFilter = filters?.[TIMELINE_FILTER_FIELDS.RELATIVE];
  const relative = relativeFilter?.value;

  if (isRelativePeriodValue(relative)) {
    return {
      ...DEFAULT_RELATIVE_PERIOD,
      ...relative,
      isRelative: true,
    };
  }

  const storedDates = filters?.[TIMELINE_FILTER_FIELDS.DATES]?.value ?? [];
  if (hasAbsoluteRange(storedDates)) {
    const presetId = isTimelinePresetValue(relative) ? relative.presetId : undefined;
    return {
      isRelative: false,
      dateRange: [moment(storedDates[0]), moment(storedDates[1])],
      dateType: 'day',
      ...(presetId ? { mode: 'preset', presetId } : {}),
    };
  }

  // Cleared period — "all time", no date bounds.
  return {
    isRelative: false,
    dateRange: null,
    dateType: 'day',
  };
};

/**
 * Resolves Timeline period filters into a millis window for Firestore queries.
 * `null` means the user cleared the period on purpose: query without
 * createdAtDevice bounds ("all time").
 */
export const resolveTimelinePeriodMillis = filters => {
  const relative = filters?.[TIMELINE_FILTER_FIELDS.RELATIVE]?.value;

  if (isRelativePeriodValue(relative)) {
    return resolvePeriodToMillis({
      ...DEFAULT_RELATIVE_PERIOD,
      ...relative,
      isRelative: true,
    });
  }

  const storedDates = filters?.[TIMELINE_FILTER_FIELDS.DATES]?.value ?? [];
  if (!hasAbsoluteRange(storedDates)) {
    return null;
  }

  return [new Date(storedDates[0]).getTime(), new Date(storedDates[1]).getTime()];
};

/**
 * Reshapes Timeline sessionStorage payloads (including legacy flat search/dates).
 * Labels and flags always come from `defaults` (i18n).
 */
export const normalizeStoredTimelineFilters = (stored, defaults = INIT_FILTERS) => {
  if (!stored) {
    return defaults;
  }

  if (isFilterValuesFormat(stored)) {
    const relativeRaw = stored.relative?.value ?? stored.relative;
    // Stored filters are authoritative: a cleared period must stay cleared.
    const normalizedRelative = normalizeStoredRelative(
      relativeRaw && typeof relativeRaw === 'object' && 'value' in relativeRaw
        ? relativeRaw.value
        : relativeRaw,
      stored.relative?.valueTranslated,
    );

    const storedRecordTypes = stored.recordTypes?.value;
    const recordTypesValue = Array.isArray(storedRecordTypes)
      ? storedRecordTypes.filter(value => typeof value === 'string')
      : [];

    return {
      ...defaults,
      search: {
        ...defaults.search,
        value: typeof stored.search?.value === 'string' ? stored.search.value : '',
        valueTranslated:
          typeof stored.search?.valueTranslated === 'string' ? stored.search.valueTranslated : '',
      },
      dates: {
        ...defaults.dates,
        value: Array.isArray(stored.dates?.value) ? stored.dates.value : defaults.dates.value,
        valueTranslated:
          typeof stored.dates?.valueTranslated === 'string' ? stored.dates.valueTranslated : '',
      },
      relative: {
        ...defaults.relative,
        value: normalizedRelative.value,
        valueTranslated: normalizedRelative.valueTranslated,
        valueType: normalizedRelative.valueType,
      },
      entity: {
        ...defaults.entity,
        value: typeof stored.entity?.value === 'string' ? stored.entity.value : '',
        valueTranslated:
          typeof stored.entity?.valueTranslated === 'string' ? stored.entity.valueTranslated : '',
      },
      recordTypes: {
        ...defaults.recordTypes,
        value: recordTypesValue,
        valueTranslated:
          stored.recordTypes?.valueTranslated || formatRecordTypesTranslated(recordTypesValue),
      },
    };
  }

  const storedDates = formatStoredDates(stored.dates);
  const normalizedRelative = normalizeStoredRelative(stored.relative);

  return {
    ...defaults,
    search: {
      ...defaults.search,
      value: typeof stored.search === 'string' ? stored.search : '',
    },
    dates: {
      ...defaults.dates,
      value: storedDates.value,
      valueTranslated: storedDates.valueTranslated,
    },
    relative: {
      ...defaults.relative,
      value: normalizedRelative.value,
      valueTranslated: normalizedRelative.valueTranslated,
      valueType: normalizedRelative.valueType,
    },
  };
};

export const PATHS = {
  BASE_URL,
  EVENT_DETAIL: `${BASE_URL}/event/:divId/:eventId`,
  EVENT_DRAWER: `${BASE_URL}/drawer/:divId/:eventId`,
  TEMP_CREATE: `${BASE_URL}/event/create`,
  TIMELINE_EVENT_DRAWER: `${BASE_URL}/event/drawer/:divId/:scheduleId`,
};

export const getRouteToEvent = (divId, eventId = '') =>
  PATHS.EVENT_DETAIL.replace(':divId', divId).replace(':eventId', eventId);

/** Shareable Timeline drawer path (list stays mounted under the overlay). */
export const getRouteToEventDrawer = (divId, eventId = '') =>
  PATHS.EVENT_DRAWER.replace(':divId', divId).replace(':eventId', eventId);
